import React, {
  ChangeEvent,
  FC,
  forwardRef,
  useRef,
  useImperativeHandle,
} from "react";
import classNames from "classnames";

import { SelectionPill, Tooltip } from "..";
import { CURRENCIES } from "../../../constants";
import { Currency, CurrencyType } from "../../../types/InvoiceTypes";

type Props = {
  className?: string;
  dataTest?: string;
  name: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  allowedCurrencies?: Currency[];
  value?: Currency;
  disabledValues?: Currency[];
  currencyType?: CurrencyType;
  otherComingSoon?: boolean;
};

export const CurrencySelect: FC<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      dataTest,
      name: instanceName,
      onChange,
      allowedCurrencies = [],
      value,
      disabledValues,
      currencyType,
      otherComingSoon,
    },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => innerRef.current!, []);

    const handleSelect = (selectedCurrency: Currency) => {
      const event = {
        target: {
          value: selectedCurrency,
          name: instanceName,
        },
      };

      onChange(event as ChangeEvent<HTMLInputElement>);
    };

    return (
      <div
        className={classNames("flex gap-3 flex-wrap", className)}
        ref={innerRef}
        data-name={instanceName}
        data-test={dataTest}
      >
        {CURRENCIES.filter(
          ({ value, type }) =>
            (!currencyType || type === currencyType) &&
            allowedCurrencies.includes(value)
        ).map(({ value: symbol, icon, name }) => (
          <Tooltip position="bottom" text={name} key={`${name}-${symbol}`}>
            <SelectionPill
              key={`${instanceName}-${symbol}`}
              selected={symbol === value}
              text={symbol.toUpperCase()}
              icon={
                <img
                  src={icon}
                  alt={`${symbol.toUpperCase()}`}
                  className={classNames(
                    "w-6 h-6 rounded-full object-cover object-center",
                    {
                      "opacity-100": symbol === value,
                      "opacity-50": symbol !== value,
                    }
                  )}
                />
              }
              onClick={() => handleSelect(symbol)}
              dataTest={`${dataTest}-${symbol}`}
              disabled={disabledValues?.includes(symbol)}
            />
          </Tooltip>
        ))}

        {otherComingSoon && (
          <SelectionPill text="Others coming soon" disabled />
        )}
      </div>
    );
  }
);
